import { createAsyncThunk } from '@reduxjs/toolkit'
import doAsync from '../../infrastructure/doAsync'
import { createFilterString } from '../../helpers/createFilterString'
import { REGIONS } from '../../widgets/busyIndicator'

export const fetchCustomers = createAsyncThunk(
	'fetch/customers',
	async ({ filters, useCaching = true, noBusySpinner } = {}, thunkArgs) => {
		const filterQs = createFilterString(filters)
		const fullQs = filterQs ? `?${filterQs}` : ''
		return await doAsync({
			url: `api/customers${fullQs}`,
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.FETCH_CUSTOMER_ACCOUNTS,
			errorMessage: 'Unable to load customers. Please try again later.',
			...thunkArgs,
		})
	}
)

export const fetchCustomerUsers = createAsyncThunk(
	'fetch/customerUsers',
	async ({ id, useCaching = true, noBusySpinner } = {}, thunkArgs) =>
		await doAsync({
			url: `api/customers/${id}/users`,
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.FETCH_CUSTOMER_USERS,
			errorMessage: 'Unable to load customer users. Please try again later.',
			...thunkArgs,
		})
)

export const deleteCustomerUser = createAsyncThunk(
	'delete/customerUser',
	async ({ userId, data, useCaching = true, noBusySpinner } = {}, thunkArgs) =>
		await doAsync({
			url: `api/customers/user/${userId}`,
			httpMethod: 'delete',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.DELETE_CUSTOMER_USER,
			errorMessage: 'Unable to delete user. Please try again later.',
			...thunkArgs,
		})
)

export const resetCustomerUserPassword = createAsyncThunk(
	'reset/customerUserPassword',
	async (data, thunkArgs, { useCaching = true, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/users/${data.id}/password`,
			httpMethod: 'put',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.RESET_CUSTOMER_USER_PASSWORD,
			errorMessage: 'Unable to update password. Please try again later.',
			...thunkArgs,
		})
)

export const sendPasswordResetLink = createAsyncThunk(
	'send/passwordResetLink',
	async (data, thunkArgs, { noBusySpinner } = {}) =>
		await doAsync({
			url: `api/users/${encodeURIComponent(data.id)}/send-password-reset`,
			httpMethod: 'post',
			httpConfig: {
				body: JSON.stringify({ id: data.id, isResendInvite: !!data.isResendInvite }),
			},
			useCaching: false,
			noBusySpinner,
			busyIndicatorName: REGIONS.RESET_CUSTOMER_USER_PASSWORD,
			errorMessage: 'Unable to send password reset link. Please try again later.',
			...thunkArgs,
		})
)

export const updateCustomerAssignment = createAsyncThunk(
	'update/customerAssignment',
	async (data, thunkArgs, { useCaching = true, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/customers/assignment`,
			httpMethod: 'put',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.UPDATE_CUSTOMER_ASSIGNMENT,
			errorMessage:
				'Unable to assign customer to another user. Please try again later.',
			...thunkArgs,
		})
)

export const saveCustomer = createAsyncThunk(
	'save/customer',
	async (data, thunkArgs, { useCaching = true, noBusySpinner } = {}) =>
		await doAsync({
			url: 'api/accounts',
			httpMethod: 'post',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.CREATE_CUSTOMER,
			errorMessage: 'Unable to create customer. Please try again later.',
			...thunkArgs,
		})
)

export const fetchPlans = createAsyncThunk(
	'fetch/plans',
	async ({ useCaching = true, noBusySpinner } = {}, thunkArgs) => {
		return await doAsync({
			url: 'api/customers/plans',
			useCaching,
			noBusySpinner,
			errorMessage: 'Unable to load plans. Please try again later.',
			...thunkArgs,
		})
	}
)

export const fetchFFLs = createAsyncThunk(
	'api/accounts/accountId/stores',
	async ({ id, useCaching = true, noBusySpinner } = {}, thunkArgs) =>
		await doAsync({
			url: `api/accounts/${id}/stores`,
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.FETCH_VARIANCE_DATA,
			errorMessage: 'Unable to load FFLs . Please try again later.',
			...thunkArgs,
		})
)
